import {
	firestore as db, functions
} from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { encodeAndTokenizeForQueries } from '@jaymathew/utils/Search'
import {
	collection,
	DocumentData,
	getDocs,
	limit,
	orderBy,
	query,
	QueryDocumentSnapshot,
	startAfter,
	where
} from 'firebase/firestore'
import { httpsCallable, HttpsCallableResult } from 'firebase/functions'
import { Notification, NotificationInput } from '../resources/types'
import { getSelectedCompanyId } from 'shared/functions/selectedCompanyAccess'
import { getCurrentUser } from 'shared/functions/currentUserAccess'

export interface GetNotificationsReturnType<T> {
	payload: T[],
	lastDocument?: QueryDocumentSnapshot<DocumentData>
}

export const getUsersNotifications = async (sortBy: keyof Notification, sort: 'asc' | 'desc' = 'desc', limitNumber?: number, searchTerm?: string, lastDocument?: QueryDocumentSnapshot<DocumentData>): Promise<{ payload: Notification[], lastDocument?: QueryDocumentSnapshot<DocumentData> }> => {
	const currentUser = getCurrentUser()
	const companyID = getSelectedCompanyId() ?? ''
	const collectionRef = collection(db, `companies/${companyID}/users/${currentUser?.uid}/notifications`)
	let notificationsQuery = query(collectionRef, orderBy(sortBy.toString(), sort))

	if (searchTerm) {
		const encodedSearchTerm = await encodeAndTokenizeForQueries(searchTerm)
		notificationsQuery = query(notificationsQuery, where('searchTerms', 'array-contains-any', encodedSearchTerm))
	}

	if (lastDocument) {
		notificationsQuery = query(notificationsQuery, startAfter(lastDocument))
	}

	if (limitNumber !== undefined) {
		notificationsQuery = query(notificationsQuery, limit(limitNumber))
	}

	const notificatonsCollection = await getDocs(notificationsQuery)

	if (notificatonsCollection.empty) {
		return {
			payload: [],
			lastDocument: undefined,
		}
	}

	const notificatons: Notification[] = notificatonsCollection.docs.map((doc) => doc.data() as Notification)

	return {
		payload: notificatons,
		lastDocument: notificatonsCollection.docs[notificatonsCollection.docs.length - 1],
	}
}

export const getCompanyNotifications = async (sortBy: keyof NotificationInput, sort: 'asc' | 'desc' = 'desc', limitNumber?: number, searchTerm?: string, lastDocument?: QueryDocumentSnapshot<DocumentData>): Promise<{ payload: NotificationInput[], lastDocument?: QueryDocumentSnapshot<DocumentData> }> => {
	const companyID = getSelectedCompanyId() ?? ''
	const collectionRef = collection(db, `companies/${companyID}/campaigns`)
	let notificationsQuery = query(collectionRef, orderBy(sortBy.toString(), sort))

	if (searchTerm) {
		const encodedSearchTerm = await encodeAndTokenizeForQueries(searchTerm)
		notificationsQuery = query(notificationsQuery, where('searchTerms', 'array-contains-any', encodedSearchTerm))
	}

	if (lastDocument) {
		notificationsQuery = query(notificationsQuery, startAfter(lastDocument))
	}

	if (limitNumber !== undefined) {
		notificationsQuery = query(notificationsQuery, limit(limitNumber))
	}


	const notificatonsCollection = await getDocs(notificationsQuery)

	if (notificatonsCollection.empty) {
		return {
			payload: [],
			lastDocument: undefined,
		}
	}

	const notificatons: NotificationInput[] = notificatonsCollection.docs.map((doc) => doc.data() as NotificationInput)

	return {
		payload: notificatons,
		lastDocument: notificatonsCollection.docs[notificatonsCollection.docs.length - 1],
	}

}


export interface CreateNotificationsInput {
	notification: Partial<NotificationInput>,
}
export const createNotifications = async ({ notification }: CreateNotificationsInput) => {
	const companyID = getSelectedCompanyId() ?? ''
	const callable = httpsCallable(functions, 'firebasefuncCreateNotifications')

	const result = await callable({
		companyID,
		...notification,
	})

	return result
}

export const updateUserNotification = async ({
	notificationID, dismissed, read, deleted,
}: { notificationID: string, dismissed?: boolean, read?: boolean, deleted?: boolean }) => {
	const companyID = getSelectedCompanyId() ?? ''
	// create the Ref to the cloud callable function.
	const cloudfunction = httpsCallable(functions, 'firebasefuncEditUserNotification')

	// call the function with the parms
	const result: HttpsCallableResult = await cloudfunction({
		companyID,
		notificationID,
		dismissed,
		read,
		deleted,
	})

	// return what the cloud function returned
	return result
}
